import { useState, useRef, useEffect, useCallback } from 'react';
import { ShieldCheckIcon, SparklesIcon, FireIcon, TrophyIcon, BoltIcon, StarIcon, CheckCircleIcon, ArrowRightIcon, FunnelIcon } from '@heroicons/react/24/solid';
import type { ComponentType, SVGProps } from 'react';
import { useContent } from '../../context/ContentContext';
import type { PlanItem } from '../../types/cms';

const PLAN_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [ShieldCheckIcon, SparklesIcon, FireIcon, TrophyIcon, FunnelIcon];

function PlanRow({ plan, icon, isActive, onClick, isAnnual }: { plan: PlanItem; icon: ComponentType<SVGProps<SVGSVGElement>>; isActive: boolean; onClick: () => void; isAnnual: boolean }) {
  const Icon = icon;
  const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
  const originalPrice = isAnnual ? plan.annualOriginal : plan.monthlyOriginal;
  const { settings } = useContent();

  return (
    <div
      className={`w-full text-left rounded-xl border transition-all duration-300 p-4 sm:p-5 ${
        isActive
          ? 'border-brand-orange/25 bg-white shadow-md shadow-brand-navy/5'
          : 'border-gray-200/80 bg-white/60 hover:bg-white hover:border-gray-300/80 hover:shadow-sm'
      }`}
    >
      <button type="button" onClick={onClick} aria-expanded={isActive}
        aria-controls={isActive ? `plan-${plan.name}-details` : undefined}
        className="flex w-full items-center gap-3 sm:gap-4 text-left">
        <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
          isActive ? 'bg-brand-orange' : 'bg-brand-navy/[0.06]'
        }`}>
          <Icon className={`w-5 h-5 transition-colors duration-300 ${isActive ? 'text-white' : 'text-brand-navy/40'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-base sm:text-lg font-bold transition-colors duration-300 ${isActive ? 'text-brand-navy' : 'text-brand-navy/70'}`}>
              {plan.name} Plan
            </span>
            {plan.badge && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-orange/10 text-brand-orange px-2.5 py-0.5 rounded-full">
                {plan.badge}
              </span>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          {plan.priceNote ? (
            <span className={`text-sm font-semibold transition-colors duration-300 ${isActive ? 'text-brand-orange' : 'text-brand-navy/70'}`}>
              {plan.priceNote}
            </span>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${isActive ? 'text-brand-orange' : 'text-brand-navy/70'}`}>
                  {price}
                </span>
                <span className="text-brand-warm-gray text-xs">/mo</span>
              </div>
              {originalPrice && (
                <span className="text-brand-warm-gray/60 text-xs line-through">{originalPrice}/mo</span>
              )}
              {isAnnual && (
                <span className="text-brand-orange/70 text-[10px] font-semibold">{plan.annualTotal}/yr</span>
              )}
            </>
          )}
        </div>
      </button>

      {isActive && (
        <div id={`plan-${plan.name}-details`} className="border-t border-gray-100 pt-4 sm:pt-5 mt-4 sm:mt-5 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
            {plan.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <CheckCircleIcon className="w-4 h-4 text-brand-orange flex-shrink-0" />
                <span className="text-brand-navy/70 text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <a
            href={settings.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 bg-brand-navy text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-brand-orange transition-all duration-300"
          >
            Get Started
            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      )}
    </div>
  );
}

function LegacyCard() {
  const { content, settings } = useContent();
  const legacyPlan = content.plansGrid.legacyPlan;

  return (
    <div className="rounded-2xl border border-brand-navy/10 bg-brand-navy overflow-hidden relative">
      <div className="absolute inset-0 opacity-[0.07]">
        <div className="absolute top-0 right-0 w-72 h-72 bg-brand-orange rounded-full blur-3xl translate-x-1/4 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-brand-tan rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
      </div>

      <div className="relative p-6 sm:p-8 lg:p-10">
        <div className="flex items-start justify-between gap-4 mb-4 sm:mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-orange flex items-center justify-center flex-shrink-0">
              <BoltIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-white">{legacyPlan.name}</h3>
              <p className="text-white/40 text-xs sm:text-sm">{legacyPlan.priceNote}</p>
            </div>
          </div>
          {legacyPlan.badge && (
            <span className="flex items-center gap-1.5 bg-brand-orange/15 text-brand-orange pl-3 pr-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex-shrink-0">
              <StarIcon className="w-3 h-3" />
              {legacyPlan.badge}
            </span>
          )}
        </div>

        {legacyPlan.description && (
          <p className="text-white/45 text-sm leading-relaxed mb-5 sm:mb-6">{legacyPlan.description}</p>
        )}

        <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div>
            <span className="text-3xl sm:text-4xl font-bold text-brand-orange">{legacyPlan.price}</span>
            {legacyPlan.savings && (
              <div className="inline-flex items-center gap-1.5 bg-brand-orange/10 border border-brand-orange/20 rounded-full px-3 py-1 ml-3">
                <span className="text-brand-orange font-bold text-xs">SAVE {legacyPlan.savings}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 sm:mb-8">
          {legacyPlan.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-orange flex-shrink-0" />
              <span className="text-sm font-medium text-white/70">{feature}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
          <a
            href={settings.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2.5 bg-brand-orange text-white px-7 py-3.5 rounded-lg font-semibold text-sm hover:bg-white hover:text-brand-navy transition-all duration-300"
          >
            Get Started Today
            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
            className="inline-flex items-center justify-center gap-2 text-white/50 font-semibold text-sm hover:text-brand-orange transition-colors duration-300 py-2"
          >
            Call {settings.phone}
          </a>
        </div>

        <div className="border-t border-white/[0.06] pt-6">
          <div className="grid grid-cols-3 gap-3">
            {legacyPlan.stats.map((stat) => (
              <div key={stat.label} className="bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 py-4 text-center">
                <p className="text-brand-orange font-bold text-lg leading-tight">{stat.value}</p>
                <p className="text-white/35 text-[10px] uppercase tracking-wider font-semibold mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BillingToggle({ isAnnual, onToggle }: { isAnnual: boolean; onToggle: () => void }) {
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const annualRef = useRef<HTMLButtonElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    const activeBtn = isAnnual ? annualRef.current : monthlyRef.current;
    if (activeBtn) {
      setIndicator({
        left: activeBtn.offsetLeft,
        width: activeBtn.offsetWidth,
      });
    }
  }, [isAnnual]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  return (
    <div className="flex items-center justify-center mb-8 sm:mb-10">
      <div className="relative inline-flex items-center border-2 border-brand-navy/10 rounded-full p-1.5 bg-brand-off-white">
        <div
          className="absolute top-1.5 bottom-1.5 rounded-full bg-brand-orange shadow-md transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            left: `${indicator.left}px`,
            width: `${indicator.width}px`,
          }}
        />
        <button
          ref={monthlyRef}
          aria-pressed={!isAnnual}
          onClick={() => !isAnnual || onToggle()}
          className={`relative z-10 px-6 sm:px-8 py-2.5 rounded-full text-sm font-bold tracking-wide transition-colors duration-300 ${
            !isAnnual ? 'text-white' : 'text-brand-navy/40 hover:text-brand-navy/60'
          }`}
        >
          Monthly
        </button>
        <button
          ref={annualRef}
          aria-pressed={isAnnual}
          onClick={() => isAnnual || onToggle()}
          className={`relative z-10 px-6 sm:px-8 py-2.5 rounded-full text-sm font-bold tracking-wide transition-colors duration-300 whitespace-nowrap ${
            isAnnual ? 'text-white' : 'text-brand-navy/40 hover:text-brand-navy/60'
          }`}
        >
          Annual (Save 20%)
        </button>
      </div>
    </div>
  );
}

export default function PlansGrid() {
  const [activeIndex, setActiveIndex] = useState(2);
  const [isAnnual, setIsAnnual] = useState(false);
  const { content } = useContent();
  const d = content.plansGrid;

  return (
    <section id="plans-grid" className="relative bg-brand-off-white">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-24 xl:py-36 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-[220px] xl:top-[240px] 2xl:top-[310px]">
              <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-brand-orange rounded-full" />
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                  {d.sectionLabel}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
                {d.heading}
              </h2>

              <p className="text-base sm:text-lg text-brand-warm-gray leading-relaxed mb-8 sm:mb-10">
                {d.description}
              </p>

              <div className="hidden sm:block relative mb-8 sm:mb-10">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-orange to-brand-orange/20 rounded-full" />
                <div className="pl-6 sm:pl-8">
                  <p className="text-lg sm:text-xl font-serif italic text-brand-navy/70 leading-relaxed mb-2">
                    &quot;{d.quote}&quot;
                  </p>
                  <p className="text-sm font-semibold text-brand-orange tracking-wide uppercase">{d.quoteLabel}</p>
                </div>
              </div>

              <div className="hidden sm:flex items-stretch gap-0">
                <div className="flex items-center gap-3 pr-6">
                  <ShieldCheckIcon className="w-5 h-5 text-brand-navy/40 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-brand-navy leading-tight">{d.statsJobsCompleted}</p>
                    <p className="text-brand-warm-gray text-xs">Jobs Completed</p>
                  </div>
                </div>
                <div className="w-px bg-brand-navy/10 self-stretch" />
                <div className="flex items-center gap-3 px-6">
                  <StarIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-brand-orange leading-tight">{d.statsSatisfaction}</p>
                    <p className="text-brand-warm-gray text-xs">Satisfaction</p>
                  </div>
                </div>
                <div className="w-px bg-brand-navy/10 self-stretch" />
                <div className="flex items-center gap-3 pl-6">
                  <CheckCircleIcon className="w-5 h-5 text-brand-navy/40 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-brand-navy leading-tight">{d.statsGoogleRating}</p>
                    <p className="text-brand-warm-gray text-xs">Google Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <BillingToggle isAnnual={isAnnual} onToggle={() => setIsAnnual(!isAnnual)} />

            <div className="space-y-3 mb-8 sm:mb-10">
              {d.plans.map((plan, index) => (
                <PlanRow
                  key={plan.name}
                  plan={plan}
                  icon={PLAN_ICONS[index] || ShieldCheckIcon}
                  isActive={activeIndex === index}
                  isAnnual={isAnnual}
                  onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
                />
              ))}
            </div>

            <p className="text-brand-warm-gray/60 text-xs font-semibold uppercase tracking-widest mb-4">5-Year Commitment</p>
            <LegacyCard />
          </div>
        </div>
      </div>
    </section>
  );
}
